import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Card,
  CardBody,
  Col,
  Container,
  Row,
  TabContent,
  TabPane,
  Button,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  Progress,
} from 'reactstrap';
import BreadCrumb from '../../Components/Common/BreadCrumb';
import {
  getPropertyById,
  getRentCollectorsByPropertyId,
  removeProperty,
} from '../../services/propertyService';
import {
  deleteSubProperty,
  exportSubProperties,
  getSubPropertiesByPropertyId,
  uploadSubProperties,
} from 'services/subPropertyService';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import CreatePropertyModel from './create-property-model';
import ConfirmDialog from 'Components/Common/ConfirmDialog';
import SubPropertyModal from './sub-property-modal';
import { useDispatch } from 'react-redux';
import { isVisible } from 'slices/loader/reducer';
import {
  formatFloorLabel,
  getApiErrorMessage,
  handleViewFile,
} from 'utility/utilityFunctions';
import { Property } from 'utility/interface';
import AssignRentCollectorModal from './assign-rent-collector-modal';
import { generateFilename } from 'utility/filenameGenerator';
import { getNavigationBreadcrumbs } from 'utility/breadcrumbUtils';
import { getFileViewUrl } from 'services/fileService';
import BulkExcelActions, {
  BulkExcelLoadingOverlay,
} from 'Components/Common/BulkExcelActions';
import { useBulkExcelOperations } from 'utility/excelBulkOperations';

const PropertyDetails = () => {
  const { id, propertyId } = useParams();
  const navigate = useNavigate();
  const [property, setProperty] = useState<Property>(null);
  const [deleteModal, setDeleteModal] = useState(false);
  const [editModal, setEditModal] = useState(false);
  const [subProperty, setSubProperty] = useState<any>(null);
  const [subProperties, setSubProperties] = useState<any[]>([]);
  const [subPropertyModal, setSubPropertyModal] = useState(false);
  const [subPropertyToEdit, setSubPropertyToEdit] = useState<any>(null);
  const [subPropertyToDelete, setSubPropertyToDelete] = useState<any>(null);
  const [subPropertyDeleteModal, setSubPropertyDeleteModal] = useState(false);
  const [rentCollectors, setRentCollectors] = useState<any[]>([]);
  const [isAssignRentCollectorModalOpen, setIsAssignRentCollectorModalOpen] =
    useState(false);

  const dispatch = useDispatch();
  const searchParams = new URLSearchParams(window.location.search);
  const activeTab = '1';
  const mazarId = searchParams.get('mazar_id');
  const mazarName = searchParams.get('mazar_name');
  const propertyName = searchParams.get('property_name');

  const land = property?.land;

  const registration = property?.land?.registration;

  const landId = land?.id;

  const surveyNumber = land?.surveyNumber || 'Land';

  const trustRegId = registration?.id;

  const trustRegNumber = registration?.trustRegNumber || 'Unregistered';

  const category = registration?.category || 'unregistered';

  const breadCrumbItems = [
    ...getNavigationBreadcrumbs({
      organizationId: mazarId || '',
      mazarName,
      category,
      trustRegNumber,
      trustRegId: trustRegId?.toString(),
      surveyNumber,
      landId: landId?.toString(),
      includeSurvey: true,
    }),

    {
      label: propertyName || property?.name || '-',
      path: `/properties/${id}?mazar_name=${mazarName}&property_name=${propertyName}&mazar_id=${mazarId}&trustRegId=${trustRegId}&surveyNumber=${surveyNumber}&landId=${landId}&category=${category}`,
      isActive: true,
    },
  ];

  // Calculate occupancy percentage for progress bar
  const occupancyPercentage =
    property && property.tenantsCount
      ? (property.subPropertiesWithActiveTenants / property.tenantsCount) * 100
      : 0;

  const toggleEditModal = () => {
    setEditModal(!editModal);
  };

  // Sub Property handlers
  const toggleSubPropertyModal = () => {
    setSubPropertyModal(prev => {
      if (prev) setSubPropertyToEdit(null);
      return !prev;
    });
  };

  const loadSubProperties = async () => {
    dispatch(isVisible(true));
    try {
      const result = await getSubPropertiesByPropertyId(id || '');

      if (result?.data?.length) {
        setSubProperties(result.data);
      } else {
        setSubProperties([]);
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message, {
        autoClose: 2000,
      });
    } finally {
      dispatch(isVisible(false));
    }
  };

  const loadRentCollectors = async () => {
    try {
      const result = await getRentCollectorsByPropertyId(id || '');
      if (result?.data) setRentCollectors(result.data);
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message, {
        autoClose: 2000,
      });
    }
  };

  const loadPropertyDetails = async () => {
    dispatch(isVisible(true));
    try {
      const result = await getPropertyById(id || '');
      if (result?.data) {
        setProperty(result.data);
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message, {
        autoClose: 2000,
      });
    } finally {
      dispatch(isVisible(false));
    }
  };

  const onClickDelete = () => {
    setDeleteModal(true);
  };

  const handleDeleteProperty = async () => {
    dispatch(isVisible(true));
    try {
      await removeProperty(id || '');
      toast.success('Property deleted successfully', { autoClose: 2000 });
      setDeleteModal(false);
      navigate('/properties');
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Property delete failed'), {
        autoClose: 2000,
      });
    } finally {
      dispatch(isVisible(false));
    }
  };

  const handleDeleteSubProperty = async () => {
    if (!subPropertyToDelete?.id) return;

    dispatch(isVisible(true));
    try {
      await deleteSubProperty(subPropertyToDelete.id);
      toast.success('Sub property deleted successfully', { autoClose: 2000 });
      setSubPropertyDeleteModal(false);
      setSubPropertyToDelete(null);
      loadSubProperties();
      loadPropertyDetails();
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Sub property delete failed'), {
        autoClose: 2000,
      });
    } finally {
      dispatch(isVisible(false));
    }
  };

  const { loading, handleExport, handleUpload } = useBulkExcelOperations({
    exportFile: () => exportSubProperties(id ? Number(id) : undefined),
    getExportFilename: () =>
      generateFilename({
        organizationName: mazarName || 'Mazar',
        category: category || 'unregistered',
        trustRegNumber: trustRegNumber || 'Orphan',
        surveyNumber: surveyNumber || 'Survey',
        propertyName: propertyName || 'Property',
      }),
    uploadFile: uploadSubProperties,
    onUploadSuccess: () => loadSubProperties(),
  });

  useEffect(() => {
    loadPropertyDetails();
    loadSubProperties();
    loadRentCollectors();
  }, [id, landId]);

  if (!property) {
    return <div>Loading...</div>;
  }

  return (
    <React.Fragment>
      {loading && <BulkExcelLoadingOverlay />}
      <div
        className="page-content"
        style={{ maxHeight: 'calc(100vh - 20px)', overflowY: 'auto' }}
      >
        <ToastContainer closeButton={false} limit={1} />
        <ConfirmDialog
          isOpen={deleteModal}
          title="Confirm Delete"
          message="Are you sure you want to delete this property?"
          confirmLabel="Yes, Delete"
          confirmBtnColor="danger"
          iconClass="ri-delete-bin-line text-danger"
          onConfirm={handleDeleteProperty}
          onCancel={() => setDeleteModal(false)}
        />
        <ConfirmDialog
          isOpen={subPropertyDeleteModal}
          title="Confirm Delete"
          message={`Are you sure you want to delete "${subPropertyToDelete?.name || 'this sub property'}"?`}
          confirmLabel="Yes, Delete"
          confirmBtnColor="danger"
          iconClass="ri-delete-bin-line text-danger"
          onConfirm={handleDeleteSubProperty}
          onCancel={() => {
            setSubPropertyDeleteModal(false);
            setSubPropertyToDelete(null);
          }}
        />
        <Container fluid>
          <BreadCrumb
            title={subProperty?.name || property?.name || 'Property Details'}
            breadcrumbItems={breadCrumbItems}
          />
          <Row>
            <Col lg={12}>
              <Card>
                <CardBody>
                  <div className="d-flex justify-content-between align-items-center mb-4">
                    <h4 className="card-title mb-0">{property.name}</h4>
                    <div className="d-flex gap-2">
                      <Button
                        color="primary"
                        className="me-2"
                        onClick={() => setIsAssignRentCollectorModalOpen(true)}
                      >
                        Manage Rent Collectors
                      </Button>
                      <Button color="primary" onClick={toggleEditModal}>
                        <i className="ri-edit-line align-bottom me-1"></i> Edit
                        Property
                      </Button>
                      <Button color="danger" onClick={onClickDelete}>
                        <i className="ri-delete-bin-line align-bottom me-1"></i>{' '}
                        Delete Property
                      </Button>
                    </div>
                  </div>

                  {/* Quick Stats */}
                  <Row className="mb-4 g-3">
                    {[
                      { label: 'Floors', value: property.floor },
                      {
                        label: 'Rent Collectors',
                        value: rentCollectors.length,
                      },
                      {
                        label: 'Occupancy',
                        value: `${property.subPropertiesWithActiveTenants}/${property.tenantsCount}`,
                      },
                      {
                        label: 'Cellar',
                        value: property.cellar ? 'Yes' : 'No',
                      },
                    ].map((stat, idx) => (
                      <Col lg={3} md={6} key={idx}>
                        <Card className="shadow-sm border-0 h-100 bg-light overflow-hidden">
                          <CardBody className="text-center">
                            <h2 className="mb-0 fw-semibold text-primary">
                              {stat.value ?? '-'}
                            </h2>
                            <p className="text-muted mb-0">{stat.label}</p>
                          </CardBody>
                        </Card>
                      </Col>
                    ))}
                  </Row>

                  <TabContent activeTab={activeTab} className="p-3 text-muted">
                    <TabPane tabId="1">
                      <Row>
                        {/* Basic & Additional Information Side-by-Side */}
                        <Col lg={6}>
                          <Card className="shadow-sm border-0 mb-4 h-100 overflow-hidden">
                            <CardBody>
                              <h5 className="mb-3">
                                <i className="ri-information-line align-middle me-1 text-primary"></i>
                                Basic Information
                              </h5>
                              <table className="table table-sm mb-0">
                                <tbody>
                                  <tr>
                                    <th scope="row" style={{ width: '200px' }}>
                                      Address
                                    </th>
                                    <td>{property.address}</td>
                                  </tr>
                                  <tr>
                                    <th scope="row">Size (sq.ft)</th>
                                    <td>{property.size ?? 'Not specified'}</td>
                                  </tr>
                                  <tr>
                                    <th scope="row">Total Jantri</th>
                                    <td>
                                      {property.totalJantri ?? 'Not specified'}
                                    </td>
                                  </tr>
                                  {/* Added Total Number of Floors */}
                                  <tr>
                                    <th scope="row">Total Number of Floors</th>
                                    <td>{property.floor ?? 'Not specified'}</td>
                                  </tr>
                                  {/* Added Total Number of Tenants */}
                                  <tr>
                                    <th scope="row">Total Number of Tenants</th>
                                    <td>
                                      {property.tenantsCount ?? 'Not specified'}
                                    </td>
                                  </tr>
                                  {/* Occupancy Progress */}
                                  <tr>
                                    <th scope="row">Occupancy</th>
                                    <td style={{ minWidth: '200px' }}>
                                      <div className="mb-1">
                                        {property?.subPropertiesWithActiveTenants ||
                                          0}
                                        /{property?.tenantsCount || 0}
                                      </div>
                                      <Progress
                                        value={occupancyPercentage}
                                        color="success"
                                        style={{ height: '15px' }}
                                      >
                                        {occupancyPercentage.toFixed(0)}%
                                      </Progress>
                                    </td>
                                  </tr>
                                  {/* Added Cellar Information */}
                                  <tr>
                                    <th scope="row">
                                      Trust Registration Number
                                    </th>
                                    <td>
                                      {property.trustRegNumber ||
                                        'Not available'}
                                    </td>
                                  </tr>
                                  <tr>
                                    <th scope="row">Type of Acquisition</th>
                                    <td>
                                      {property.typeOfAcquisition ||
                                        'Not specified'}
                                    </td>
                                  </tr>
                                </tbody>
                              </table>
                            </CardBody>
                          </Card>
                        </Col>

                        {/* Additional Information */}
                        <Col lg={6}>
                          <Card className="shadow-sm border-0 mb-4 h-100 bg-light-subtle overflow-hidden">
                            <CardBody>
                              <h5 className="mb-3">
                                <i className="ri-list-check-2 align-middle me-1 text-primary"></i>
                                Additional Information
                              </h5>
                              <table className="table table-sm mb-0">
                                <tbody>
                                  <tr>
                                    <th scope="row" style={{ width: '200px' }}>
                                      Disputes
                                    </th>
                                    <td>
                                      {property.disputes ||
                                        'No disputes recorded'}
                                    </td>
                                  </tr>
                                  <tr>
                                    <th scope="row">Minutes of Meeting</th>
                                    <td>
                                      {property.minutesOfMeeting ||
                                        'No minutes recorded'}
                                    </td>
                                  </tr>
                                </tbody>
                              </table>
                            </CardBody>
                          </Card>
                        </Col>

                        {/* Documents & Photos */}
                        <Col lg={12}>
                          <h5 className="mb-3 mt-3">Documents & Images</h5>

                          {/* Document cards */}
                          <Row className="g-3 mb-4">
                            {(() => {
                              const raw = [
                                {
                                  label: 'Property Card',
                                  val: property.propertyCard,
                                  icon: 'ri-bank-card-2-line',
                                },
                                {
                                  label: 'PTR Document',
                                  val: property.PTR,
                                  icon: 'ri-article-line',
                                },
                                {
                                  label: 'House Tax Bill',
                                  val: property.houseTaxBill,
                                  icon: 'ri-home-gear-line',
                                },
                                {
                                  label: 'Acquisition Docs',
                                  val: property.acquisitionDocument,
                                  icon: 'ri-article-line',
                                },
                                {
                                  label: 'Non-Agricultural Certificate',
                                  val: property.nonAgriculturalCertificate,
                                  icon: 'ri-file-text-line',
                                },
                                {
                                  label: 'Construction Permissions',
                                  val: property.constructionPermissions,
                                  icon: 'ri-building-line',
                                },
                                {
                                  label: 'Building Plan Approvals',
                                  val: property.buildingPlanApprovals,
                                  icon: 'ri-file-chart-line',
                                },
                                {
                                  label: 'Fee Payment Receipts',
                                  val: property.feePaymentReceipts,
                                  icon: 'ri-money-dollar-circle-line',
                                },
                                {
                                  label: 'Environmental Clearance',
                                  val: property.environmentalClearance,
                                  icon: 'ri-leaf-line',
                                },
                                {
                                  label: 'Fire Safety Certificate',
                                  val: property.fireSafetyCertificate,
                                  icon: 'ri-fire-line',
                                },
                              ];
                              return raw.map((d, i) => {
                                const links = d.val ? d.val.split(',') : [];
                                return (
                                  <Col xs={6} md={3} key={i}>
                                    <Card className="border-0 shadow-sm h-100 text-center hover-shadow overflow-hidden">
                                      <CardBody className="py-4">
                                        <i
                                          className={`${d.icon} fs-32 text-primary mb-2`}
                                        />
                                        <h6 className="fs-13 fw-semibold mb-2">
                                          {d.label}
                                        </h6>
                                        {links.length ? (
                                          <>
                                            {links.map((l, idx) => (
                                              <Button
                                                key={idx}
                                                size="sm"
                                                color="primary"
                                                className="me-1 mb-1"
                                                onClick={() =>
                                                  handleViewFile(l)
                                                }
                                              >
                                                {links.length > 1
                                                  ? `File ${idx + 1}`
                                                  : 'View'}
                                              </Button>
                                            ))}
                                          </>
                                        ) : (
                                          <span className="badge bg-warning-subtle text-warning">
                                            Not uploaded
                                          </span>
                                        )}
                                      </CardBody>
                                    </Card>
                                  </Col>
                                );
                              });
                            })()}
                          </Row>

                          {/* Property Photos */}
                          <h6 className="mb-3">Property Photos</h6>
                          {property.photos ? (
                            <Row>
                              {property.photos.split(',').map((p, idx) => (
                                <Col
                                  key={idx}
                                  lg={3}
                                  md={4}
                                  sm={6}
                                  className="mb-3"
                                >
                                  <div className="position-relative">
                                    <img
                                      src={getFileViewUrl(p)}
                                      alt={`Property Photo ${idx + 1}`}
                                      className="img-fluid rounded"
                                      style={{
                                        height: '200px',
                                        width: '100%',
                                        objectFit: 'cover',
                                      }}
                                    />
                                    <Button
                                      color="primary"
                                      size="sm"
                                      className="position-absolute top-0 end-0 m-2"
                                      onClick={() => handleViewFile(p)}
                                    >
                                      <i className="ri-eye-line"></i>
                                    </Button>
                                  </div>
                                </Col>
                              ))}
                            </Row>
                          ) : (
                            <p className="text-muted">No photos uploaded.</p>
                          )}
                        </Col>
                      </Row>
                    </TabPane>
                  </TabContent>
                </CardBody>
              </Card>
            </Col>
          </Row>

          {/* Sub Properties Section */}
          <Row className="mt-4">
            <Col lg={12}>
              <Card className="overflow-hidden">
                <CardBody>
                  <div className="d-flex justify-content-between align-items-start mb-4">
                    <h5 className="mb-0">Sub Properties</h5>

                    <div className="d-flex flex-column align-items-end gap-2">
                      <Button
                        color="primary"
                        onClick={() => {
                          setSubPropertyToEdit(null);
                          setSubPropertyModal(true);
                        }}
                        disabled={
                          (property?.tenantsCount || 0) <=
                          (subProperties?.length || 0)
                        }
                      >
                        <i className="ri-add-line align-bottom me-1"></i>
                        Add Sub Property
                      </Button>

                      <BulkExcelActions
                        helpTargetId="property-details-bulk-upload-help"
                        onDownload={handleExport}
                        onUpload={handleUpload}
                        helpClassName="text-muted fw-medium mb-2 d-inline-flex align-items-center gap-1"
                        disabled={loading}
                        downloadDisabled={loading || subProperties.length < 1}
                      />
                    </div>
                  </div>
                  <Row className="g-3">
                    {subProperties.length ? (
                      subProperties.map(sp => (
                        <Col lg={4} md={6} key={sp.id}>
                          <Card className="border-0 shadow-sm h-100 subprop-hover position-relative">
                            <UncontrolledDropdown className="position-absolute end-0 me-2 mt-2">
                              <DropdownToggle
                                tag="span"
                                className="cursor-pointer text-muted"
                              >
                                <i className="ri-more-2-fill" />
                              </DropdownToggle>
                              <DropdownMenu className="dropdown-menu-end">
                                <DropdownItem
                                  onClick={() => {
                                    setSubPropertyToEdit(sp);
                                    setSubPropertyModal(true);
                                  }}
                                >
                                  <i className="ri-edit-line me-2"></i> Edit
                                </DropdownItem>
                                <DropdownItem
                                  className="text-danger"
                                  onClick={() => {
                                    setSubPropertyToDelete(sp);
                                    setSubPropertyDeleteModal(true);
                                  }}
                                >
                                  <i className="ri-delete-bin-line me-2"></i>{' '}
                                  Delete
                                </DropdownItem>
                              </DropdownMenu>
                            </UncontrolledDropdown>
                            {/* status strip */}
                            <div
                              style={{
                                height: 6,
                                background: sp.tenants.length
                                  ? '#45cb85'
                                  : '#f06548',
                              }}
                            />
                            <CardBody>
                              <div className="d-flex align-items-start gap-3">
                                <span className="avatar-sm bg-primary-subtle text-primary rounded d-flex align-items-center justify-content-center flex-shrink-0">
                                  <i className="ri-building-2-line fs-20" />
                                </span>
                                <div className="flex-grow-1 overflow-hidden">
                                  <h5
                                    className="fs-16 fw-semibold mb-1 cursor-pointer text-truncate"
                                    onClick={() => {
                                      navigate(
                                        `/properties/${property.id}/sub-properties/${sp.id}` +
                                          `?mazar_name=${encodeURIComponent(mazarName || '')}` +
                                          `&mazar_id=${mazarId}` +
                                          `&property_id=${property.id}` +
                                          `&property_name=${encodeURIComponent(property.name)}` +
                                          `&sub_property_name=${encodeURIComponent(sp.name || '')}` +
                                          `&trustRegNumber=${trustRegNumber}` +
                                          `&trustRegId=${trustRegId}` +
                                          `&surveyNumber=${surveyNumber}` +
                                          `&landId=${landId}` +
                                          `&category=${category}`,
                                      );
                                      setSubProperty(sp);
                                    }}
                                  >
                                    {sp.name}
                                  </h5>
                                  <p className="text-muted small mb-0 text-truncate">
                                    Floor: {formatFloorLabel(sp.floor) || '-'}
                                  </p>
                                  <p className="text-muted small mb-0 text-truncate">
                                    Shop: {sp.shopNumber || '-'}
                                  </p>
                                  <p
                                    className={`small mb-0 text-truncate ${sp.tenants.length ? 'text-success' : 'text-danger'}`}
                                  >
                                    {sp.tenants.length
                                      ? `Tenant: ${sp.tenants[0].tenantName}`
                                      : 'No Tenant'}
                                  </p>
                                </div>
                              </div>
                            </CardBody>
                          </Card>
                        </Col>
                      ))
                    ) : (
                      <Col>
                        <div className="text-center py-5">
                          <h4>No sub properties available</h4>
                        </div>
                      </Col>
                    )}
                  </Row>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>

        <CreatePropertyModel
          isOpen={editModal}
          toggle={toggleEditModal}
          organizationId={property.organizationId}
          organizationName={mazarName || ''}
          trustRegNumber={trustRegNumber}
          surveyNumber={surveyNumber || ''}
          landName={property.name || ''}
          onSuccess={loadPropertyDetails}
          editData={property}
          landId={Number(landId)}
        />

        <AssignRentCollectorModal
          isOpen={isAssignRentCollectorModalOpen}
          toggle={() => setIsAssignRentCollectorModalOpen(false)}
          property={property}
          organizationId={property.organizationId}
          onSuccess={loadPropertyDetails}
        />

        {/* Sub Property Modal */}
        <SubPropertyModal
          props={property}
          isOpen={subPropertyModal}
          toggle={toggleSubPropertyModal}
          parentPropertyId={Number(id)}
          organizationName={mazarName || ''}
          trustRegNumber={trustRegNumber}
          surveyNumber={surveyNumber || ''}
          propertyName={property?.name || ''}
          onSuccess={() => {
            loadSubProperties();
            loadPropertyDetails();
          }}
          editData={subPropertyToEdit}
        />
      </div>
    </React.Fragment>
  );
};

export default PropertyDetails;
